import React, { useEffect, useState } from "react";
import { useLazyQuery, useMutation, useQuery } from "@apollo/client";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import { GET_EMAIL_SETTINGS, GET_SERVICE_ITEMS } from "../../../../utils/GraphQL/queries";
import {
  DELETE_SERVICE_ITEM,
  UPDATE_SERVICE_ITEM,
  ADD_SERVICE_ITEM,
  REORDER_SERVICE_ITEMS,
  UPDATE_SERVICES_ITEM_DOOR_EQUIVALENT,
} from "../../../../utils/GraphQL/mutations";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import "./servicesManagement.css";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { payBucketLabel } from "../../../../utils/payBucketsClient";
import CheckIcon from "@mui/icons-material/Check";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import DragIndicatorIcon from "@mui/icons-material/DragIndicator";
import UpdateServiceItemModal from "../../adminComponents/UpdateServiceItemModal/UpdateServiceItemModal";
import BdrButton from "../../../../components/BdrButton/BdrButton";

const blankItem = {
  itemName: "",
  itemId: "",
  discountApplicable: false,
  index: "",
  price: "",
  priceStr: "",
  jobType: "Refinish",
  // No default bucket (2026-10-06): a new item's pay must be chosen.
  payBucket: "",
  customPayCents: null,
  requiresPhotos: true,
};

// Sort key helper — keeps the catalog rendered (and reordered) by `order`.
const byOrder = (a, b) => (a?.order ?? 0) - (b?.order ?? 0);

// A single draggable catalog row. The whole row lifts, but the drag only
// starts from the left handle (listeners are scoped to the handle button) so
// the Edit/Delete buttons stay clickable.
// Door-equivalent inline editor (inventory hardening Phase 4, plan D22 /
// section 2.10). Weight of ONE unit of this line in the door-margin report's
// doors-done denominator: a front door 1, a sidelight maybe 0.4, a tip or
// trip charge is never a door (the report ignores those buckets — the value
// is shown greyed). Click → type → Enter/blur saves; Escape cancels.
const DoorEquivalentCell = ({ item, onSave }) => {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState("");
  const isDoorish = /refinish|maintenance/i.test(item?.jobType || "") && !/trip\s*charge/i.test(item?.itemName || "") && item?.itemName !== "Tip";
  const shown = item?.doorEquivalent != null ? String(Number(item.doorEquivalent)) : "1";
  const commit = async () => {
    setEditing(false);
    const n = parseFloat(val);
    if (!Number.isFinite(n) || n < 0) return;
    if (n.toFixed(4) === Number(item?.doorEquivalent ?? 1).toFixed(4)) return;
    await onSave(item, n.toFixed(4));
  };
  if (!editing) {
    return (
      <button
        type="button"
        className="ser-man-door-eq"
        style={{ opacity: isDoorish ? 1 : 0.45 }}
        title="Door equivalent — click to edit"
        onClick={() => {
          setVal(shown);
          setEditing(true);
        }}
      >
        {shown}
      </button>
    );
  }
  return (
    <input
      className="ser-man-door-eq-input"
      autoFocus
      value={val}
      inputMode="decimal"
      onChange={(e) => setVal(e.target.value.replace(/[^0-9.]/g, ""))}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") setEditing(false);
      }}
    />
  );
};

const SortableRow = ({ item, onEdit, onDelete, onDoorEquivalent }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item?.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.4 : 1,
  };

  // Job type short label (see legacy notes): T=Tip, HC=Hardware Change,
  // TC=Trip Charge, O=Other, R=Refinish, M=Maintenance, N/A=none. Falls back
  // to the raw jobType so misconfigured rows are visible.
  const jt = item?.jobType || "";
  const type =
    item?.itemName === "Tip"
      ? "T"
      : !jt
        ? "N/A"
        : /hardware change/i.test(jt)
          ? "HC"
          : /trip\s*charge/i.test(jt)
            ? "TC"
            : /^\s*other\s*$/i.test(jt)
              ? "O"
              : /refinish/i.test(jt)
                ? "R"
                : /maintenance/i.test(jt)
                  ? "M"
                  : jt;

  // Pay bucket (2026-10-06): the stored bucket's title. A Custom row shows
  // its per-unit amount (blank = 100% of price); a legacy 'other' row says
  // so (its next save converts it); a null row says "Not set" plus what the
  // server's fallback actually pays it as today (effectivePayBucket).
  const bucketDisplay = (() => {
    const b = item?.payBucket;
    if (b === "custom") {
      return item?.customPayCents != null && String(item.customPayCents) !== ""
        ? `Custom · ${priceStrFormatter(item.customPayCents)}/unit`
        : "Custom · 100% of price";
    }
    if (b === "refinish" || b === "half" || b === "full" || b === "other") {
      return payBucketLabel(b);
    }
    return `Not set (auto: ${payBucketLabel(item?.effectivePayBucket)})`;
  })();

  // requiresPhotos: null treated as "required" per safe default.
  const photosDisplay = item?.requiresPhotos === false ? "No" : "Yes";

  return (
    <tr ref={setNodeRef} style={style} {...attributes}>
      <td className="bdr-table-drag">
        <button
          type="button"
          className="bdr-drag-handle"
          aria-label="Drag to reorder"
          title="Drag to reorder"
          {...listeners}
        >
          <DragIndicatorIcon fontSize="small" />
        </button>
      </td>
      <td data-label="Item">{item?.itemName}</td>
      <td data-label="Apply Discount">
        {item?.discountApplicable ? (
          <span className="bdr-text-accent">
            <CheckIcon />
          </span>
        ) : (
          <span className="bdr-text-danger">
            <strong>X</strong>
          </span>
        )}
      </td>
      <td data-label="Price">{priceStrFormatter(item?.price)}</td>
      <td data-label="Job Type">{type} </td>
      <td data-label="Pay Bucket">{bucketDisplay}</td>
      <td data-label="Photos?">{photosDisplay}</td>
      <td data-label="Doors">
        <DoorEquivalentCell item={item} onSave={onDoorEquivalent} />
      </td>
      <td className="bdr-table-actions">
        <BdrButton
          variant="plain"
          size="sm"
          iconOnly
          icon={<EditIcon />}
          ariaLabel="Edit service item"
          onClick={() => onEdit(item)}
        />
        <BdrButton
          variant="plain"
          size="sm"
          iconOnly
          icon={<DeleteIcon />}
          className="bdr-text-danger"
          ariaLabel="Delete service item"
          confirm
          confirmTitle="Delete this service item?"
          confirmLabel="Delete"
          note={`"${item?.itemName}" will be removed from the catalog. Quotes and invoices that already use it keep their line, but it can't be added to new ones.`}
          onClick={() => onDelete(item?.id)}
        />
      </td>
    </tr>
  );
};

const ServicesManagement = () => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  const [servicesItems, setServicesItems] = useState([]);
  const [openModal, setOpenModal] = useState(false);
  const [createItem, setCreateItem] = useState(false);
  const [editItemObj, setEditItemObj] = useState({ ...blankItem });
  const [servicesFetch] = useLazyQuery(GET_SERVICE_ITEMS);
  const [deleteCall] = useMutation(DELETE_SERVICE_ITEM);
  const [updateCall] = useMutation(UPDATE_SERVICE_ITEM);
  const [createItemCall] = useMutation(ADD_SERVICE_ITEM);
  const [reorderCall] = useMutation(REORDER_SERVICE_ITEMS);
  const [doorEqCall] = useMutation(UPDATE_SERVICES_ITEM_DOOR_EQUIVALENT);
  // Which item the Maintenance Program starting price (email templates) is
  // linked to, if any (2026-09-27). A price change on that item cascades to
  // the emails server-side; the confirms below just tell the user first.
  const {
    data: emailSettingsData,
    error: emailSettingsError,
    refetch: refetchEmailSettings,
  } = useQuery(GET_EMAIL_SETTINGS, { fetchPolicy: "cache-and-network" });
  // Without the settings we can't tell which item is linked, so the linked-
  // item confirms can't show; say so once rather than skipping them silently.
  useEffect(() => {
    if (emailSettingsError) {
      handleSnack(
        "Couldn't check which item sets the Maintenance Program price; saves still update it.",
        5000
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSettingsError]);
  const maintLinkId =
    emailSettingsData?.getEmailSettings?.maintProgramServicesItemId || null;
  const maintCents = emailSettingsData?.getEmailSettings?.maintProgramStartingCents;

  const saveDoorEquivalent = async (item, doorEquivalent) => {
    try {
      const { data } = await doorEqCall({ variables: { servicesItemId: item?.id, doorEquivalent } });
      const saved = data?.updateServicesItemDoorEquivalent?.doorEquivalent;
      setServicesItems((prev) => prev.map((it) => (it?.id === item?.id ? { ...it, doorEquivalent: saved ?? doorEquivalent } : it)));
      handleSnack(`${item?.itemName}: ${Number(saved ?? doorEquivalent)} door${Number(saved ?? doorEquivalent) === 1 ? "" : "s"} each`, 2000);
    } catch (err) {
      handleSnack(err?.message || "Couldn't save the door equivalent", 3000);
    }
  };

  // Drag starts only after a small movement so a click on the handle doesn't
  // immediately pick up the row; keyboard sensor for accessibility.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    window.scrollTo(0, 0);
    servicesCall();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const servicesCall = async () => {
    const { data } = await servicesFetch({
      fetchPolicy: "network-only",
    });

    if (data?.getServicesItems) {
      // Keep state sorted by `order` so the rendered order === drag order.
      setServicesItems([...data.getServicesItems].sort(byOrder));
    }
  };

  // Save handlers return true on success, false otherwise. The modal only
  // closes on true, so failure paths must NOT reset editItemObj (the modal
  // re-hydrates from it and would blank the form it is keeping open).
  const addItem = async (item) => {
    try {
      const { data } = await createItemCall({
        variables: {
          itemName: item?.itemName,
          discountApplicable: item?.discountApplicable,
          price: item?.price,
          jobType: item?.jobType,
          payBucket: item?.payBucket || null,
          customPayCents: item?.customPayCents ?? null,
          requiresPhotos:
            typeof item?.requiresPhotos === "boolean" ? item.requiresPhotos : null,
        },
      });

      if (data?.addServicesItem) {
        // No editItemObj reset here: the modal's close() → closeModal does
        // it; resetting now would blank the still-open form.
        handleSnack("Item Created", 2000);
        // Server assigns order = max + 1, so it sorts to the bottom.
        setServicesItems((prev) => [...prev, data?.addServicesItem].sort(byOrder));
        return true;
      }
      handleSnack("Something went wrong...Please try again", 2000);
    } catch (err) {
      handleSnack(err?.message || "Something went wrong...Please try again", 5000);
    }
    return false;
  };

  const editItem = async (updatedObj) => {
    const current = servicesItems.find((it) => it?.id === updatedObj?.itemId);
    const isLinked = !!maintLinkId && updatedObj?.itemId === maintLinkId;
    const priceChanged = String(updatedObj?.price ?? "") !== String(current?.price ?? "");

    // D5: changing the linked item's price also changes the email price.
    // Asked while the edit modal is still open; Cancel keeps the edits.
    if (isLinked && priceChanged) {
      const ok = await confirm({
        title: "Change Maintenance Program price?",
        message: `${current?.itemName || updatedObj?.itemName} sets the Maintenance Program starting price in quote and invoice emails. Saving changes it from ${priceStrFormatter(maintCents ?? current?.price)} to ${priceStrFormatter(updatedObj?.price)}.`,
        confirmLabel: "Save",
        cancelLabel: "Cancel",
      });
      if (!ok) return false;
    }

    try {
      const { data } = await updateCall({
        variables: {
          itemId: updatedObj?.itemId,
          itemName: updatedObj?.itemName,
          discountApplicable: updatedObj?.discountApplicable,
          price: updatedObj?.price,
          jobType: updatedObj?.jobType,
          payBucket: updatedObj?.payBucket || null,
          customPayCents: updatedObj?.customPayCents ?? null,
          requiresPhotos:
            typeof updatedObj?.requiresPhotos === "boolean"
              ? updatedObj.requiresPhotos
              : null,
        },
      });

      if (!data?.updateServicesItem) {
        handleSnack("Something went wrong...Please try again", 2000);
        return false;
      }

      // editItemObj is reset by closeModal once the modal closes on `true`;
      // resetting here would blank the form during the refetch below.
      setServicesItems((prev) => {
        const filtered = prev?.map((item) => {
          if (item?.id !== updatedObj?.itemId) {
            return item;
          }
          return { ...data?.updateServicesItem };
        });
        return [...filtered].sort(byOrder);
      });

      // The cascade to the email price is best-effort server-side; re-read
      // and warn if it didn't land (plan F6).
      if (isLinked && priceChanged) {
        let settings = null;
        try {
          settings = (await refetchEmailSettings())?.data?.getEmailSettings;
        } catch (_) {
          settings = null;
        }
        if (!settings) {
          handleSnack("Item saved; couldn't confirm the email starting price updated.", 6000);
          return true;
        }
        if (settings.maintProgramStartingCents !== data.updateServicesItem.price) {
          handleSnack(
            "Item saved, but the email starting price did not update. Re-select the item on the Email Templates tab.",
            6000
          );
          return true;
        }
      }
      handleSnack("Item Updated", 2000);
      return true;
    } catch (err) {
      handleSnack(err?.message || "Something went wrong...Please try again", 6000);
      return false;
    }
  };

  const deleteItem = async (itemId) => {
    const item = servicesItems.find((it) => it?.id === itemId);
    const isLinked = !!maintLinkId && itemId === maintLinkId;
    // D4: every delete confirms; the linked item also warns about the unlink.
    const ok = await confirm({
      title: `Delete ${item?.itemName || "this item"}?`,
      message: isLinked
        ? `This item sets the Maintenance Program starting price in quote and invoice emails. Deleting it unlinks the price, which stays at ${priceStrFormatter(maintCents ?? item?.price)}.`
        : "This removes it from the services catalog.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      destructive: true,
    });
    if (!ok) return;

    try {
      const { data } = await deleteCall({
        variables: {
          itemId,
        },
      });

      if (data?.deleteServicesItem === true) {
        handleSnack("Item Deleted", 2000);
        setServicesItems((prev) => {
          const filtered = prev?.filter((it) => it.id !== itemId);

          return [...filtered];
        });
        if (isLinked) refetchEmailSettings().catch(() => {});
        return;
      }
      handleSnack("Something went wrong...Please try again", 2000);
    } catch (err) {
      handleSnack(err?.message || "Something went wrong...Please try again", 5000);
    }
  };

  // Drop handler: reorder the local array optimistically, renumber `order`,
  // persist the new id sequence, and revert if the server write fails.
  const handleDragEnd = async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = servicesItems.findIndex((i) => i?.id === active.id);
    const newIndex = servicesItems.findIndex((i) => i?.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const prevOrder = servicesItems;
    const reordered = arrayMove(servicesItems, oldIndex, newIndex).map(
      (it, idx) => ({ ...it, order: idx })
    );
    setServicesItems(reordered); // optimistic

    try {
      await reorderCall({
        variables: { orderedIds: reordered.map((i) => i?.id) },
      });
      handleSnack("Order Updated", 1500);
    } catch (err) {
      console.error("[reorderServicesItems]", err);
      setServicesItems(prevOrder); // revert on failure
      handleSnack("Reorder failed — order reverted", 2500);
    }
  };

  const closeModal = () => {
    setOpenModal(false);
    setEditItemObj({ ...blankItem });
  };

  return (
    <div className="bdr-page">
      <UpdateServiceItemModal
        close={closeModal}
        selectedItemObj={editItemObj}
        open={openModal}
        updateServiceItem={editItem}
        createItem={createItem}
        createItemFunc={addItem}
      />
      <div className="bdr-panel bdr-grad-container-med bdr-border-std bdr-shadow-depth">
        <div className="bdr-row-end">
          <BdrButton
            variant="confirm"
            size="sm"
            icon={<AddIcon sx={{ fontSize: 18 }} />}
            onClick={() => {
              setCreateItem(true);
              setOpenModal(true);
            }}
          >
            New Item
          </BdrButton>
        </div>
        <DndContext
          sensors={sensors}
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >
          <table className="bdr-table">
            <thead>
              <tr>
                <th className="bdr-table-drag"></th>
                <th scope="col">Item</th>
                <th scope="col">Apply Discount</th>
                <th scope="col">Price</th>
                <th scope="col">Job Type</th>
                <th scope="col">Pay Bucket</th>
                <th scope="col">Photos?</th>
                <th scope="col" title="Door equivalent per unit — the door-margin report's denominator">Doors</th>
                <th scope="col">Actions</th>
              </tr>
            </thead>
            <tbody>

              <SortableContext
                items={servicesItems.map((i) => i?.id)}
                strategy={verticalListSortingStrategy}
              >
                {servicesItems?.map((item) => (
                  <SortableRow
                    key={item?.id}
                    item={item}
                    onEdit={(it) => {
                      setEditItemObj(it);
                      setCreateItem(false);
                      setOpenModal(true);
                    }}
                    onDelete={deleteItem}
                    onDoorEquivalent={saveDoorEquivalent}
                  />
                ))}
              </SortableContext>
            </tbody>
          </table>
        </DndContext>
      </div>
    </div>
  );
};

export default ServicesManagement;
